<script lang="ts">
  import { fetchPostThread, queueReply } from '@api';
  // Community Timeline — a Skool-style single-column feed built from the shared
  // <SkoolPost> cards, with a filter bar on top (search, author, groups, date
  // range, sort). Clicking a post opens it in a solid reading side-panel
  // (<ThreadDrawer>) — the full body + its comment thread. Filters are
  // mirrored to the URL so a filtered/searched view is shareable, and are seeded
  // back from the URL on load (incl. legacy ?label=/?date=/?post= deep-links).
    import { navigate, setQuery } from '../lib/router.svelte';
  import AdminShell from './AdminShell.svelte';
  import PageContainer from '../lib/ui/PageContainer.svelte';
  import StateBlock from '@ui/StateBlock.svelte';
  import SkoolPost from '@ui/SkoolPost.svelte';
  import ThreadDrawer from '../lib/ui/ThreadDrawer.svelte';
  import TimelineFilters from '../lib/ui/TimelineFilters.svelte';
  import PageHeader from '../lib/ui/PageHeader.svelte';
  import {
    redirectIfAwaitingOnboarding,
    getToken,
    handle401,
    localDayStartISO,
    utcDayStartISO,
    utcDayEndISO,
    localDayEndISO,
    displayDayStart,
    displayDayEnd,
    type AdminUser,
    type PostDetail,
    type LabelRow,
    type PostSort,
    type RangePreset,
  } from '../lib/admin';
  import { community, withCommunity, onCommunityChange } from '../lib/community.svelte';
  import { asAdminUser, ensureBootstrap } from '../lib/session';
  import { CURSOR_START, fetchLabels, fetchPosts, toAuthor, withNextCursor, type PostPage } from '@api';

  const PAGE = 20;

  let me = $state<AdminUser | null>(null);
  let loading = $state(true);
  let error = $state<string | null>(null);

  let list = $state<PostPage | null>(null);
  let listLoading = $state(false);
  // v1 pages the feed by CURSOR, so the page number is where in the walk we are and
  // `cursors[i]` is what fetches page `i` — see `withNextCursor`. Any filter change
  // resets both, which is also what the server requires: a cursor carries a hash of
  // the filters it was minted under.
  let cursors = $state<(string | null)[]>(CURSOR_START);
  let pageIndex = $state(0);

  // label_id → display name, for the "· category" line; full rows feed the group chips.
  let labels = $state<LabelRow[]>([]);
  let labelName = $state<Record<string, string>>({});

  // --- filter state -----------------------------------------------------------
  let q = $state('');
  let authorId = $state<number | null>(null);
  let groups = $state<string[]>([]);
  let preset = $state<RangePreset>('all');
  // A custom range is held TWICE, and the split is the point. `sinceAt`/`untilAt`
  // are the canonical UTC instants — what the API is sent and what the URL carries,
  // because an instant is the only form of a "day" two components can't disagree
  // about. `since`/`until` are the `YYYY-MM-DD` the date inputs and the range chip
  // show; a `<input type="date">` renders EMPTY when handed anything else.
  // `showDayInputs` derives the display pair, `readDayInputs` folds an edit back.
  let sinceAt = $state<string | null>(null);
  let untilAt = $state<string | null>(null);
  let since = $state<string | null>(null);
  let until = $state<string | null>(null);
  let sort = $state<PostSort>('new');
  let unanswered = $state(false);

  // The post open in the reading drawer (single selection). Loaded details are
  // cached by id so reopening a post is instant.
  let selectedId = $state<number | null>(null);
  let details = $state<Record<number, PostDetail>>({});
  let detailLoading = $state(false);
  let detailError = $state<string | null>(null);

  const selectedDetail = $derived(selectedId != null ? (details[selectedId] ?? null) : null);
  const selectedRow = $derived(
    selectedId != null ? (list?.items.find((p) => p.id === selectedId) ?? null) : null,
  );
  const selectedCategory = $derived(
    selectedRow?.label_id ? (labelName[selectedRow.label_id] ?? null) : null,
  );

  let nowMs = $state(Date.now());
  $effect(() => {
    const t = setInterval(() => (nowMs = Date.now()), 30_000);
    return () => clearInterval(t);
  });

  // URL seeds — consumed once on first load, then cleared like the other filters
  // on a real community switch. Supports the new params plus the legacy
  // ?label= / ?date= / ?post= deep-links from the posts/analytics pages.
  const _qs = new URLSearchParams(window.location.search);
  const PRESETS: RangePreset[] = ['3d', '7d', '30d', '90d', 'all', 'custom'];
  const SORTS: PostSort[] = ['new', 'comments', 'upvotes'];
  // Returns the CANONICAL instants (`sinceAt`/`untilAt`); the displayed days are
  // derived from them by `showDayInputs`.
  function seedRange(): { preset: RangePreset; since: string | null; until: string | null } {
    const r = _qs.get('range');
    if (r && PRESETS.includes(r as RangePreset)) {
      if (r === 'custom') return { preset: 'custom', since: _qs.get('since'), until: _qs.get('until') };
      return { preset: r as RangePreset, since: null, until: null };
    }
    // Legacy `?date=YYYY-MM-DD` deep-link. That day-string was produced by the
    // SERVER (a heatmap cell, whose buckets are UTC days), so it is resolved as a
    // UTC day — not fed to the local-day helpers, which is what used to shift the
    // window by the viewer's offset. Current links send explicit instants above.
    const d = _qs.get('date');
    if (d && /^\d{4}-\d{2}-\d{2}$/.test(d)) {
      return { preset: 'custom', since: utcDayStartISO(d), until: utcDayEndISO(d) };
    }
    return { preset: 'all', since: null, until: null };
  }
  const _seedR = seedRange();
  let pendingQ = _qs.get('q') ?? '';
  let pendingAuthor = Number(_qs.get('author')) || null;
  let pendingGroups = (_qs.get('groups') ?? _qs.get('label') ?? '').split(',').map((s) => s.trim()).filter(Boolean);
  let pendingSort = (SORTS.includes(_qs.get('sort') as PostSort) ? _qs.get('sort') : 'new') as PostSort;
  let pendingPreset = _seedR.preset;
  let pendingSince = _seedR.since;
  let pendingUntil = _seedR.until;
  let pendingUnanswered = _qs.get('unanswered') === '1' || _qs.get('unanswered') === 'true';
  let pendingPost = Number(_qs.get('post')) || null;

  /** Adopt the URL seeds as the live filter state, and reset the walk. */
  function applySeeds(): void {
    cursors = CURSOR_START;
    pageIndex = 0;
    q = pendingQ; pendingQ = '';
    authorId = pendingAuthor; pendingAuthor = null;
    groups = pendingGroups; pendingGroups = [];
    preset = pendingPreset; pendingPreset = 'all';
    sinceAt = pendingSince; pendingSince = null;
    untilAt = pendingUntil; pendingUntil = null;
    showDayInputs();
    sort = pendingSort; pendingSort = 'new';
    unanswered = pendingUnanswered; pendingUnanswered = false;
    selectedId = null;
    details = {};
    detailLoading = false;
    detailError = null;
  }

  /**
   * The cold load, in ONE round trip — and W5 with it (doc 04 §4.8).
   *
   * The deep-linked post used to be fetched inside the list promise's `.then`:
   * `/admin/me` → `/admin/communities` → `/admin/posts` → `/admin/posts/{id}`, four
   * deep, for a URL that named the post at parse time. Doc 04 calls that a client
   * sequencing bug rather than something needing a server mechanism, and declines to
   * add `expand_post` for it — so the fix is here: the thread, the list and the label
   * vocabulary all go out together with the bootstrap, against the `localStorage`
   * community id, and `withCommunity` retries once if that id turns out to be stale.
   */
  async function init(): Promise<void> {
    if (!getToken()) {
      navigate('/admin/login');
      return;
    }
    loading = true;
    error = null;
    applySeeds();
    const seedPost = pendingPost; pendingPost = null;
    let pageErr: unknown = null;
    const pending = withCommunity(async (skoolId) => {
      const [page, labelPage, seed] = await Promise.all([
        fetchPosts(skoolId, listQuery()),
        fetchLabels(skoolId).catch(() => null),
        seedPost != null ? fetchPostThread(skoolId, seedPost).catch(() => null) : null,
      ]);
      return { skoolId, page, labelPage, seed };
    }).catch((err: unknown) => {
      pageErr = err;
      return null;
    });
    try {
      me = asAdminUser(await ensureBootstrap());
      if (redirectIfAwaitingOnboarding(me, navigate)) return;
      const got = await pending;
      if (got) {
        adoptPage(got.page);
        adoptLabels(got.labelPage?.items ?? []);
        watch.seen(got.skoolId);
        if (got.seed) {
          details = { [got.seed.post.id]: got.seed };
          selectedId = got.seed.post.id;
        }
      } else if (pageErr) {
        throw pageErr;
      }
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      error = err instanceof Error ? err.message : 'Failed to load.';
    } finally {
      loading = false;
    }
  }

  // The community whose feed is on screen; `init` loaded the first one.

  // Reload when the shared community changes; untrack paging/filter edits so they
  // don't retrigger it. `loading` guards the cold load: it is true only while `init`
  // is in flight, and without it this effect would re-issue the request already on
  // the wire the moment the bootstrap lands.
  const watch = onCommunityChange(
    (id) => {
      applySeeds();
      void loadLabels(id);
      void loadPosts();
    },
    { gate: () => !loading },
  );

  function adoptLabels(rows: LabelRow[]): void {
    labels = rows;
    const map: Record<string, string> = {};
    for (const l of rows) if (l.name) map[l.label_id] = l.name;
    labelName = map;
  }

  async function loadLabels(id: number): Promise<void> {
    try {
      adoptLabels((await fetchLabels(id)).items);
    } catch {
      adoptLabels([]);
    }
  }

  // Canonical instants -> the days the inputs show.
  function showDayInputs(): void {
    since = displayDayStart(sinceAt);
    until = displayDayEnd(untilAt);
  }

  // ...and back, for the bound the user ACTUALLY moved. The guard is the whole
  // point: `onchange` also fires for the search box, the author picker and the
  // group chips, and re-resolving an untouched bound would convert a UTC-day
  // deep-link into a local day and silently shift the window by the viewer's
  // offset — the exact bug the instants exist to prevent. A date input hands back
  // a day the VIEWER picked, so an edit resolves through the local-day helpers.
  function readDayInputs(): void {
    if (since !== displayDayStart(sinceAt)) sinceAt = since ? localDayStartISO(since) : null;
    if (until !== displayDayEnd(untilAt)) untilAt = until ? localDayEndISO(until) : null;
  }

  // Rolling presets resolve to a `since` at fetch time (so "30d" always means the
  // last 30 days); custom carries the explicit bounds resolved above. All bounds
  // are sent as UTC instants — rolling = "N days ago from now", custom = whichever
  // kind of day boundary produced it — so date filtering matches the calendar the
  // bound was picked against regardless of the viewer's UTC offset (the backend
  // forced YYYY-MM-DD to UTC days before, which made "today" wrong by the offset,
  // e.g. +2h in Berlin).
  function rangeParams(): { since: string | null; until: string | null } {
    const days: Record<string, number> = { '3d': 3, '7d': 7, '30d': 30, '90d': 90 };
    const n = days[preset];
    if (n != null) {
      return { since: new Date(Date.now() - n * 86_400_000).toISOString(), until: null };
    }
    if (preset === 'custom') return { since: sinceAt, until: untilAt };
    return { since: null, until: null };
  }

  /** The filter set, as the v1 route's query. `label_id` goes out as a REPEATED
   *  parameter rather than a comma-joined scalar (§1.6), and an unknown `sort` is a
   *  400 naming the parameter instead of a silent fall back to `new`. */
  function listQuery(cursor: string | null = null): Parameters<typeof fetchPosts>[1] {
    const { since: s, until: u } = rangeParams();
    return {
      limit: PAGE,
      cursor,
      q: q.trim(),
      memberId: authorId,
      labelIds: groups,
      since: s,
      until: u,
      sort,
      unanswered,
    };
  }

  function adoptPage(page: PostPage, index = 0): void {
    list = page;
    pageIndex = index;
    cursors = withNextCursor(cursors, index, page.next_cursor);
  }

  async function loadPosts(index = 0): Promise<void> {
    if (community.selectedId == null) return;
    if (index < 0 || index >= cursors.length) return;
    listLoading = true;
    error = null;
    try {
      adoptPage(await fetchPosts(community.selectedId, listQuery(cursors[index] ?? null)), index);
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      error = err instanceof Error ? err.message : 'Failed to load posts.';
    } finally {
      listLoading = false;
    }
  }

  // Mirror the active filters to the URL (canonicalised — drop the legacy single
  // params once the page owns richer state).
  function syncUrl(): void {
    setQuery({
      q: q.trim() || null,
      author: authorId != null ? String(authorId) : null,
      groups: groups.length ? groups.join(',') : null,
      range: preset === 'all' ? null : preset,
      // Instants, not the displayed days: a URL gets shared, and a bare day-string
      // means something different to whoever opens it.
      since: preset === 'custom' ? sinceAt || null : null,
      until: preset === 'custom' ? untilAt || null : null,
      sort: sort === 'new' ? null : sort,
      unanswered: unanswered ? '1' : null,
      label: null,
      date: null,
      post: null,
    });
  }

  function applyFilters(): void {
    // A cursor is only valid for the filters it was minted under, so the walk starts
    // over — the server would answer `cursor_filter_mismatch` otherwise, and rows
    // from a different filter set would be worse than the error.
    cursors = CURSOR_START;
    pageIndex = 0;
    readDayInputs();
    syncUrl();
    void loadPosts();
  }

  // Open the reading drawer for a post; fetch its thread unless already cached.
  async function openPost(id: number): Promise<void> {
    selectedId = id;
    detailError = null;
    if (details[id]) return;
    if (community.selectedId == null) return;
    detailLoading = true;
    try {
      const d = await fetchPostThread(community.selectedId, id);
      details = { ...details, [id]: d };
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      detailError = err instanceof Error ? err.message : 'Failed to load the thread.';
    } finally {
      detailLoading = false;
    }
  }
  /**
   * Queue a reply to whatever the reader aimed the box at — the same
   * `POST …/outbox/items` upsert the moderation inbox writes, so it lands in the one
   * queue the Outbox surface already reviews and sends. Rethrows so the composer can
   * render the server's own sentence; only 401 is swallowed, by the navigate.
   */
  async function queueFromDrawer(
    to: { kind: 'post' | 'comment'; id: number },
    text: string,
  ): Promise<void> {
    if (community.selectedId == null) return;
    try {
      await queueReply(community.selectedId, to.kind, to.id, text);
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      throw err;
    }
  }

  function closeDrawer(): void {
    selectedId = null;
  }

  const total = $derived(list?.total ?? 0);
  const from = $derived(pageIndex * PAGE);

  function prevPage(): void {
    if (pageIndex === 0) return;
    void loadPosts(pageIndex - 1);
  }
  function nextPage(): void {
    if (pageIndex + 1 >= cursors.length) return;
    void loadPosts(pageIndex + 1);
  }

  void init();
</script>

<AdminShell {me}>
  {#if loading}
    <StateBlock state="loading" />
  {:else if error && !list}
    <StateBlock state="error" message={error} onretry={init} />
  {:else}
    <!-- No page-local background: the shell's content column already paints the grey
         canvas. This page laid `bg-base-200/60` over it, which is the only reason the
         Timeline read a shade lighter than every other page. -->
    <PageContainer>
      <!-- This page's title was `text-xl` where its siblings were `text-3xl`, which
           is how a page ends up feeling like a subsection of itself. -->
      <PageHeader section="Insights" title="Timeline">
        {#snippet titleExtra()}
          <span class="font-mono text-sm font-normal text-base-content/62">{total.toLocaleString()} posts</span>
        {/snippet}
      </PageHeader>
      <div class="mb-5">
        <TimelineFilters
          skoolId={community.selectedId}
          {labels}
          bind:q
          bind:authorId
          bind:groups
          bind:preset
          bind:since
          bind:until
          bind:sort
          bind:unanswered
          matched={total}
          onchange={applyFilters}
        />
      </div>

      {#if listLoading && !list}
        <StateBlock state="loading" compact />
      {:else if !list || list.items.length === 0}
        <StateBlock
          state="empty"
          message="No posts"
          hint="Nothing matches these filters."
          compact
        />
      {:else}
        <!-- `?? null` on the selectable fields: a v1 field the request did not
             select is OMITTED, not null, so it arrives as `undefined` — which the
             card's props (typed `T | null`) reject under exactOptionalPropertyTypes. -->
        <div class="flex flex-col gap-3">
          {#each list.items as p (p.id)}
            <!-- privacy-ok: handed to <SkoolPost>, which marks the body it renders -->
            <SkoolPost
              author={toAuthor(p.author)}
              title={p.title}
              body={p.excerpt ?? null}
              images={p.images ?? null}
              videos={p.videos ?? null}
              mediaSide
              skoolUrl={p.url}
              upvotes={p.upvotes}
              commentCount={p.comment_count}
              createdAt={p.created_at}
              category={p.label_id ? (labelName[p.label_id] ?? null) : null}
              pinned={p.pinned}
              selected={selectedId === p.id}
              now={nowMs}
              onclick={() => openPost(p.id)}
            />
          {/each}
        </div>

        <div class="mt-5 flex items-center justify-between font-mono text-xs text-base-content/72">
          <button class="btn btn-ghost btn-sm" disabled={pageIndex === 0} onclick={prevPage}>← Prev</button>
          <span>{from + 1}–{from + list.items.length} of {total.toLocaleString()}</span>
          <button class="btn btn-ghost btn-sm" disabled={pageIndex + 1 >= cursors.length} onclick={nextPage}>Next →</button>
        </div>
      {/if}
    </PageContainer>

    <!-- Reading side-panel: the clicked post, its comment thread, and a reply box. -->
    <ThreadDrawer
      open={selectedId != null}
      onclose={closeDrawer}
      skoolId={community.selectedId}
      target={selectedId != null ? { kind: 'post', id: selectedId, postId: selectedId } : null}
      detail={selectedDetail}
      loading={detailLoading}
      error={detailError}
      eyebrow={selectedCategory}
      onSubmit={queueFromDrawer}
      now={nowMs}
      onretry={() => selectedId != null && openPost(selectedId)}
    />
  {/if}
</AdminShell>
